import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import Api from '@plone/volto/helpers/Api/Api';
import AppNav from '../AppNav/AppNav';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import {
  formatMatterReferenceMessage,
  formatTokenMessage,
  sharedMessages,
} from '../../utils/uiMessages';

const CLOSED_STATUSES = new Set(['done', 'cancelled']);

const messages = defineMessages({
  actionableJobsNote: {
    id: 'All open jobs across matters, excluding finished and cancelled work.',
    defaultMessage:
      'All open jobs across matters, excluding finished and cancelled work.',
  },
  allJobsNote: {
    id: 'All jobs across matters, including finished and cancelled work.',
    defaultMessage: 'All jobs across matters, including finished and cancelled work.',
  },
  deleteJobTitle: {
    id: 'Delete job?',
    defaultMessage: 'Delete job?',
  },
  deleteJobMessage: {
    id: 'Delete "{title}"? This cannot be undone.',
    defaultMessage: 'Delete "{title}"? This cannot be undone.',
  },
  jobLookupFailed: {
    id: 'Job lookup failed with status {status}',
    defaultMessage: 'Job lookup failed with status {status}',
  },
  loadingJobs: {
    id: 'Loading jobs…',
    defaultMessage: 'Loading jobs…',
  },
  noActionableJobs: {
    id: 'No actionable jobs.',
    defaultMessage: 'No actionable jobs.',
  },
  noJobs: {
    id: 'No jobs.',
    defaultMessage: 'No jobs.',
  },
  showFinishedJobs: {
    id: 'Show finished jobs',
    defaultMessage: 'Show finished jobs',
  },
});

export default function JobsPage() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingPath, setDeletingPath] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showFinishedJobs, setShowFinishedJobs] = useState(false);

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const response = await fetch(
          '/++api++/@search?portal_type=Patent%20Job&fullobjects=1&b_size=500&metadata_fields=client_name&metadata_fields=matter_reference&metadata_fields=matter_reference_code',
          {
            headers: authHeaders({ Accept: 'application/json' }),
          },
        );
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(messages.jobLookupFailed, {
              status: response.status,
            }),
          );
        }
        const payload = await response.json();
        if (active) {
          const jobs = (payload.items || [])
            .map(normalizeJobItem)
            .sort((left, right) => {
              const leftDue = left.internal_due_date || '9999-12-31';
              const rightDue = right.internal_due_date || '9999-12-31';
              return (
                leftDue.localeCompare(rightDue) ||
                left.title.localeCompare(right.title)
              );
            });
          setItems(jobs);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [token]);

  const visibleItems = showFinishedJobs
    ? items
    : items.filter((item) => !CLOSED_STATUSES.has(item.status));

  async function onDelete(item) {
    const path = normalizeContentPath(item.url);
    setDeletingPath(path);
    setError('');

    try {
      const api = new Api();
      await api.del(path);
      setItems((current) =>
        current.filter((currentItem) => normalizeContentPath(currentItem.url) !== path),
      );
      setPendingDelete(null);
    } catch (deleteError) {
      setError(`Delete failed with status ${deleteError.status || 'unknown'}`);
    } finally {
      setDeletingPath('');
    }
  }

  return (
    <main className="patent-section">
      <section className="patent-section__shell">
        <AppNav currentPath="/jobs" />
        <header className="patent-section__hero">
          <p className="patent-section__eyebrow">Jobs</p>
          <h1>{intl.formatMessage(sharedMessages.titleActions)}</h1>
          <p className="patent-section__note">
            {intl.formatMessage(
              showFinishedJobs ? messages.allJobsNote : messages.actionableJobsNote,
            )}
          </p>
          <div className="patent-section__actions">
            <Link className="patent-section__action" to="/register-job">
              {intl.formatMessage(sharedMessages.registerJob)}
            </Link>
            <Link className="patent-section__ghost" to="/job-kind-settings">
              {intl.formatMessage(sharedMessages.editJobKinds)}
            </Link>
            <Link className="patent-section__ghost" to="/overview">
              {intl.formatMessage(sharedMessages.backToOverview)}
            </Link>
            <label className="patent-jobs-table__toggle">
              <input
                type="checkbox"
                checked={showFinishedJobs}
                onChange={(event) => setShowFinishedJobs(event.target.checked)}
              />
              <span>{intl.formatMessage(messages.showFinishedJobs)}</span>
            </label>
          </div>
        </header>

        {loading ? (
          <p className="patent-section__note">
            {intl.formatMessage(messages.loadingJobs)}
          </p>
        ) : null}
        {error ? <p className="patent-entry__error">{error}</p> : null}

        {!loading && !error ? (
          <div className="patent-jobs-table">
            {visibleItems.map((item) => (
              <article
                key={`${item.url}-${item.title}`}
                className={`patent-jobs-table__row${
                  CLOSED_STATUSES.has(item.status) ? ' patent-jobs-table__row--closed' : ''
                }`}
              >
                <div className="patent-jobs-table__primary">
                  <Link className="patent-jobs-table__title" to={jobEditorUrl(item.url)}>
                    {item.title}
                  </Link>
                  <p className="patent-jobs-table__matter-ref">
                    {intl.formatMessage(sharedMessages.matterRef)}:{' '}
                    {formatMatterReferenceMessage(intl, item.matter_reference_code)}
                  </p>
                </div>
                <div className="patent-jobs-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.internalDue)}
                  </span>
                  <strong>{item.internal_due_date}</strong>
                </div>
                <div className="patent-jobs-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.status)}
                  </span>
                  <strong>{formatTokenMessage(intl, item.status)}</strong>
                </div>
                <div className="patent-jobs-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.priority)}
                  </span>
                  <strong>{formatTokenMessage(intl, item.priority)}</strong>
                </div>
                <div className="patent-jobs-table__cell">
                  <span className="patent-jobs-table__label">
                    {intl.formatMessage(sharedMessages.hours)}
                  </span>
                  <strong>{item.estimated_hours}h</strong>
                </div>
                <div className="patent-jobs-table__cell patent-jobs-table__cell--action">
                  <button
                    className="patent-entry__ghost"
                    type="button"
                    onClick={() => setPendingDelete(item)}
                    disabled={deletingPath === normalizeContentPath(item.url)}
                  >
                    {deletingPath === normalizeContentPath(item.url)
                      ? intl.formatMessage(sharedMessages.deleting)
                      : intl.formatMessage(sharedMessages.delete)}
                  </button>
                </div>
              </article>
            ))}
            {!visibleItems.length ? (
              <p className="patent-section__note">
                {intl.formatMessage(
                  showFinishedJobs ? messages.noJobs : messages.noActionableJobs,
                )}
              </p>
            ) : null}
          </div>
        ) : null}
        <ConfirmDialog
          open={Boolean(pendingDelete)}
          title={intl.formatMessage(messages.deleteJobTitle)}
          message={
            pendingDelete
              ? intl.formatMessage(messages.deleteJobMessage, {
                  title: pendingDelete.title,
                })
              : ''
          }
          confirming={
            pendingDelete
              ? deletingPath === normalizeContentPath(pendingDelete.url)
              : false
          }
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => pendingDelete && onDelete(pendingDelete)}
        />
      </section>
    </main>
  );
}

function jobEditorUrl(url) {
  return `/job-editor?path=${encodeURIComponent(normalizeContentPath(url))}`;
}

function normalizeJobItem(item) {
  const matterReferenceCode =
    item.parent?.matter_reference_code ||
    item.matter_reference_code ||
    item.matter_reference ||
    '';
  const clientName = item.client_name || item.parent?.title || '';

  return {
    title: item.title || '',
    url: item['@id'] || item.url || '',
    client_name: clientName,
    matter_reference_code: matterReferenceCode,
    internal_due_date: item.internal_due_date || '',
    status:
      item.status?.token ||
      item.status ||
      'not_started',
    priority:
      item.priority?.token ||
      item.priority ||
      'medium',
    estimated_hours: item.estimated_hours ?? 0,
  };
}

function normalizeContentPath(url) {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    const pathname = new URL(url || '', origin).pathname;
    if (pathname === '/Plone') {
      return '/';
    }
    return pathname.startsWith('/Plone/') ? pathname.replace(/^\/Plone/, '') : pathname;
  } catch {
    if ((url || '') === '/Plone') {
      return '/';
    }
    return (url || '').startsWith('/Plone/') ? (url || '').replace(/^\/Plone/, '') : url || '';
  }
}
